import {
  FiberFanout,
  FlowDiagram,
  BridgeDiagram,
  NodeCluster,
} from "@/components/infra";

const PILLARS = [
  {
    code: "01",
    label: "DCYB-NET",
    title: "Community",
    body: "An operator-grade community for engineers, technicians, designers, builders, and operators across the digital infrastructure stack.",
    Diagram: FiberFanout,
  },
  {
    code: "02",
    label: "DCYB-FLOW",
    title: "Talent Pipeline",
    body: "Sourcing, training, and certifying the people who will build, power, cool, and run the next decade of compute.",
    Diagram: FlowDiagram,
  },
  {
    code: "03",
    label: "DCYB-LINK",
    title: "Industry Bridge",
    body: "Connecting hyperscalers, colos, OEMs, and energy partners to a vetted network of practitioners and emerging talent.",
    Diagram: BridgeDiagram,
  },
  {
    code: "04",
    label: "DCYB-NODE",
    title: "Education & Events",
    body: "High-signal programming — cohorts, certifications, IRL convenings — designed for the realities of the industry, not the slideware.",
    Diagram: NodeCluster,
  },
];

export default function Mission() {
  return (
    <section
      id="mission"
      data-testid="mission-section"
      className="border-b hairline bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-32">
        <div className="grid grid-cols-12 gap-y-12 gap-x-6 mb-16">
          <div className="col-span-12 lg:col-span-4">
            <div className="chrome-surface hidden sm:inline-flex items-center justify-center w-36 h-36 md:w-44 md:h-44 mb-8 p-4">
              <img
                src="/dcyb-logo.png"
                alt=""
                aria-hidden
                className="w-full h-full object-contain"
                draggable="false"
              />
            </div>
            <div data-testid="mission-section-label" className="font-mono-label text-[11px] text-[#0F1117] mb-6">
              / What we do
            </div>
          </div>
          <div className="col-span-12 lg:col-span-8">
            <h2 data-testid="mission-headline" className="font-display text-3xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight font-extrabold text-[#0F1117] text-balance">
              {"Developing the next generation of the "}
              <span className="text-chrome">digital infrastructure workforce.</span>
            </h2>
            <p className="mt-8 max-w-2xl text-base sm:text-lg text-[#3D4049] leading-relaxed">
              Digital infrastructure is the physical layer behind every byte. It runs on
              people — and the industry is critically short of them. DCYB exists to
              compound that workforce, in public, with the operators who will define the
              next decade.
            </p>
          </div>
        </div>

        {/* Bento grid — exposed skeleton, chrome surface */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-l border-t hairline">
          {PILLARS.map((p) => (
            <article
              key={p.code}
              data-testid={`pillar-${p.code}`}
              className="border-r border-b hairline p-8 md:p-10 bg-gradient-to-b from-white to-[#F4F5F7] hover:from-white hover:to-[#E8EAED] transition-colors group sm:min-h-[320px] flex flex-col"
            >
              <div className="flex items-start justify-between mb-8">
                <div>
                  <div className="font-mono-label text-[10px] text-[#71747B]">
                    {p.code}
                  </div>
                  <div className="font-mono-label text-[10px] text-[#0F1117] mt-1">
                    {p.label}
                  </div>
                </div>
                <p.Diagram className="h-10 w-16 text-[#0F1117] opacity-70 group-hover:opacity-100 transition-opacity" />
              </div>
              <h3 className="font-display text-2xl font-bold text-[#0F1117] mb-3 tracking-tight">
                {p.title}
              </h3>
              <p className="text-[15px] text-[#52555B] leading-relaxed">{p.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
